import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import styles from './Home.module.css';
import LoadingOverlay from '../../components/LoadingOverlay/LoadingOverlay';

import { HiOutlineBuildingOffice2, HiChevronRight } from "react-icons/hi2";
import { FiTruck, FiX, FiSend } from "react-icons/fi";
import { FaUserCog, FaUsersCog, FaTractor } from "react-icons/fa";

import { updatesData } from '../../data/updatesData';

const capitalizeFullName = (name) => {
  if (!name) return '';
  return name
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

// Cartões definidos com base nas permissões (Roles)
const cardData = [
    { to: "/sistema-imoveis", icon: <HiOutlineBuildingOffice2 className={styles.cardIcon} />, title: "Gerenciador de Imóveis", description: "Acessar o painel de propriedades e finanças.", roles: ['admin_geral', 'admin_imovel'] },
    { to: "/sistema-frota", icon: <FiTruck className={styles.cardIcon} />, title: "Sistema de Frota", description: "Controle de veículos, manutenções e rotas.", roles: ['admin_geral', 'admin_frota'] },
    
    // CORREÇÃO: Removemos 'admin_geral'. Agora SÓ o 'operador' (via filtro abaixo) pode ver este card.
    { to: "/colheita/registro-producao-operador", icon: <FiSend className={styles.cardIcon} />, title: "Registro de Produção", description: "Preencher formulário de atividade diária da máquina.", roles: ['operador'] }, 
    
    { to: "/sistema-colheita", icon: <FaTractor className={styles.cardIcon} />, title: "Gerenciamento de Produção", description: "Configuração de equipes e visualização de receitas.", roles: ['admin_geral', 'admin_frota'] },
    
    { to: "/perfil", icon: <FaUserCog className={styles.cardIcon} />, title: "Meu Perfil", description: "Alterar suas informações pessoais e senha.", roles: ['admin_geral', 'admin_imovel', 'admin_frota', 'operador', 'padrão'] },
    { to: "/gerenciar-usuarios", icon: <FaUsersCog className={styles.cardIcon} />, title: "Gerenciar Usuários", description: "Adicione, edite ou remova utilizadores.", roles: ['admin_geral'] },
];

const Home = () => {
  const userRole = (localStorage.getItem('tipo_usuario') || '').trim().toLowerCase();
  const userName = localStorage.getItem('username') || 'Usuário';
  
  const systemVersion = process.env.REACT_APP_VERSION || '0.1.0';
  const [showUpdates, setShowUpdates] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  
  const DISMISSAL_DURATION = 24 * 60 * 60 * 1000;

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 400); 

    return () => clearTimeout(timer);
  }, []); 

  useEffect(() => {
    const rawDismissalInfo = localStorage.getItem('dismissalInfo');
    
    if (!rawDismissalInfo) {
      setShowUpdates(true);
      return;
    }

    try {
      const dismissalInfo = JSON.parse(rawDismissalInfo);
      const lastDismissalTime = dismissalInfo.timestamp || 0;
      const dismissedVersion = dismissalInfo.version || '';
      const timeSinceDismissal = new Date().getTime() - lastDismissalTime;

      if (systemVersion !== dismissedVersion || timeSinceDismissal > DISMISSAL_DURATION) {
        setShowUpdates(true);
      }
    } catch (error) {
      setShowUpdates(true);
    }
  }, [systemVersion]);

  const handleDismissUpdates = () => {
    const dismissalInfo = {
      version: systemVersion,
      timestamp: new Date().getTime()
    };
    localStorage.setItem('dismissalInfo', JSON.stringify(dismissalInfo));
    setShowUpdates(false);
  };

  const capitalizedUserName = capitalizeFullName(userName);

  // --- LÓGICA DE FILTRAGEM DE CARDS (CORRIGIDA E FOCADA NO OPERADOR) ---
  const availableCards = cardData.filter(card => {
    
    if (userRole === 'operador') {
      // Operador VÊ APENAS Registro de Produção e Meu Perfil
      return card.to === '/colheita/registro-producao-operador' || card.to === '/perfil';
    }
    
    // Para todos os outros perfis (incluindo admin_geral)
    // O cartão de produção do operador não tem mais a role 'admin_geral' e não será incluído aqui.
    return card.roles.includes(userRole) || (card.roles.length === 0 && userRole === 'padrão');
  });
  // --- FIM DA LÓGICA DE FILTRAGEM ---

  return (
    <div className={styles.pageWrapper}>
      
      {isLoading && <LoadingOverlay text="Carregando..." />}

      <div className={styles.container}>
        
        <header className={styles.header}>
          <h1 className={styles.greeting}>
            Bem-vindo(a), {capitalizedUserName}!
          </h1>
          <p className={styles.subtitle}>Selecione um dos módulos abaixo para começar.</p>
        </header>

        {showUpdates && (
          <section className={styles.updatesSection}>
              <div className={styles.updatesHeader}>
                  <h2 className={styles.updatesTitle}>Novidades do Sistema</h2>
                  <div className={styles.headerActions}>
                    <span className={styles.versionBadge}>Versão {systemVersion}</span>
                    <button className={styles.dismissButton} onClick={handleDismissUpdates} title="Fechar novidades">
                        <FiX />
                    </button>
                  </div>
              </div>
              <div className={styles.updatesList}>
                  {updatesData.map((update, index) => (
                      <div key={index} className={styles.updateItem}>
                          <div className={styles.updateIconWrapper}>{update.icon}</div>
                          <div className={styles.updateContent}>
                              <h3 className={styles.updateItemTitle}>{update.title}</h3>
                              <p className={styles.updateItemDescription}>{update.description}</p>
                          </div>
                      </div>
                  ))}
              </div>
          </section>
        )}
        
        <main className={styles.grid}>
          {availableCards.map((card, index) => (
            <Link to={card.to} key={card.to} className={styles.cardLink}>
              <div className={styles.card} style={{ animationDelay: `${index * 100}ms` }}>
                <div className={styles.cardIconWrapper}>{card.icon}</div>
                <div className={styles.cardContent}>
                  <h2 className={styles.cardTitle}>{card.title}</h2>
                  <p className={styles.cardDescription}>{card.description}</p>
                </div>
                <div className={styles.cardArrowWrapper}>
                  <HiChevronRight className={styles.cardArrowIcon} />
                </div>
              </div>
            </Link>
          ))}
        </main>
      </div>
    </div>
  );
};

export default Home;